import React, { useEffect, useRef, useState } from 'react';
import { Chart } from 'chart.js/auto';
import { Paper, Group, Button, Text, Box } from '@mantine/core';

/**
 * ROI Break-Even Chart
 * Shows cumulative cost comparison: "Current Doing Business" vs AI investment across 3 scenarios.
 * Break-even = where an AI scenario line crosses below the Current Doing Business line.
 */
function ROIBreakEvenChart({ processes, contextData }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const [chartSize, setChartSize] = useState('medium');

  useEffect(() => {
    if (!canvasRef.current || !processes || processes.length === 0 || !contextData) return;
    if (chartRef.current) chartRef.current.destroy();

    const professionalServices = contextData?.professional_services_price || 0;
    const bomPrice = contextData?.bom_price || 0;
    const bomLengthMonths = contextData?.bom_length_months || 1;
    const monthlyUsageCosts = contextData?.monthly_usage_costs || 0;
    const costPerAgent = contextData?.cost_per_agent || 25;
    const monthlyBOMCost = bomLengthMonths > 0 ? bomPrice / bomLengthMonths : 0;
    const totalMonthlyCosts = monthlyBOMCost + monthlyUsageCosts;

    // ── Shared helpers (same as ROITimelineChart) ──────────────────────────
    const parseTimeToSeconds = (timeStr) => {
      if (!timeStr) return 0;
      const numMatch = timeStr.match(/[\d.]+/);
      if (!numMatch) return 0;
      const value = parseFloat(numMatch[0]);
      const lower = timeStr.toLowerCase();
      if (lower.includes('hour') || lower.includes('hr')) return value * 3600;
      if (lower.includes('min')) return value * 60;
      return value; // default: seconds
    };

    const defaultProbabilities = { Conservative: '65%', Likely: '80%', Optimistic: '95%' };
    const defaultValues = { automation_potential: '80%', number_of_interactions: '10000', avg_time_per_interaction: '30 sec' };

    // Calculate gross monthly savings for each scenario across all processes
    const cases = ['Conservative', 'Likely', 'Optimistic'];
    const grossSavings = {};
    cases.forEach((c) => {
      grossSavings[c] = processes.reduce((sum, process) => {
        const cd = process.roi_cases?.[c] || {};
        const prob = parseFloat((cd.probability || defaultProbabilities[c]).replace('%', '')) / 100;
        const autoPct = parseFloat((cd.automation_potential || defaultValues.automation_potential).replace('%', '')) / 100;
        const interactions = parseFloat((cd.number_of_interactions || defaultValues.number_of_interactions).toString().replace(/,/g, ''));
        const timeInSeconds = parseTimeToSeconds(cd.avg_time_per_interaction || defaultValues.avg_time_per_interaction);
        const automatedInteractions = interactions * prob * autoPct;
        const timeSavedHours = (automatedInteractions * timeInSeconds) / 3600;
        return sum + timeSavedHours * costPerAgent;
      }, 0);
    });

    // Net monthly savings per scenario
    const netSavings = {};
    cases.forEach((c) => { netSavings[c] = grossSavings[c] - totalMonthlyCosts; });

    // Only generate chart if at least one scenario has positive ROI
    const validCases = cases.filter(c => netSavings[c] > 0);
    if (validCases.length === 0 && professionalServices === 0) return;

    const breakEvenMonths = validCases.map(c => netSavings[c] > 0 ? professionalServices / netSavings[c] : Infinity);
    const maxBreakEven = breakEvenMonths.length > 0 ? Math.max(...breakEvenMonths.filter(isFinite)) : 0;
    const maxMonths = Math.min(Math.max(Math.ceil(maxBreakEven) + 6, 12), 36);
    const months = Array.from({ length: maxMonths + 1 }, (_, i) => i);

    // "Current Doing Business" line: cumulative agent labor cost (using Likely as the reference baseline)
    const currentGross = grossSavings['Likely'];
    const currentData = months.map(m => currentGross * m);

    // AI scenario lines: PS (upfront) + (currentGross - netSavings[scenario]) * month
    // This normalizes all scenarios to the same "current" baseline (Likely gross savings)
    const scenarioDatasets = cases.map((c) => {
      const growthRate = currentGross - netSavings[c]; // = currentGross - grossSavings[c] + totalMonthlyCosts
      const data = months.map(m => m === 0 ? professionalServices : professionalServices + growthRate * m);
      const COLORS = {
        Conservative: { border: 'rgba(239, 68, 68, 1)', bg: 'rgba(239, 68, 68, 0.08)' },
        Likely:       { border: 'rgba(234, 179, 8, 1)',  bg: 'rgba(234, 179, 8, 0.08)' },
        Optimistic:   { border: 'rgba(34, 197, 94, 1)',  bg: 'rgba(34, 197, 94, 0.08)' },
      };
      return {
        label: `With AI — ${c}`,
        data,
        borderColor: COLORS[c].border,
        backgroundColor: COLORS[c].bg,
        borderWidth: 2.5,
        fill: false,
        tension: 0,
        pointRadius: 0,
        pointHoverRadius: 5,
        borderDash: c === 'Conservative' ? [6, 3] : c === 'Optimistic' ? [3, 3] : undefined,
      };
    });

    const ctx = canvasRef.current.getContext('2d');
    chartRef.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels: months,
        datasets: [
          {
            label: 'Current Doing Business',
            data: currentData,
            borderColor: 'rgba(99, 102, 241, 1)',
            backgroundColor: 'rgba(99, 102, 241, 0.08)',
            borderWidth: 3,
            fill: false,
            tension: 0,
            pointRadius: 0,
            pointHoverRadius: 5,
          },
          ...scenarioDatasets,
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 2,
        plugins: {
          title: {
            display: true,
            text: 'Break-Even Analysis: Current Doing Business vs AI Investment',
            font: { size: 16, weight: 'bold' },
          },
          legend: { display: true, position: 'top' },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.dataset.label}: $${ctx.parsed.y.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`,
            },
          },
        },
        scales: {
          x: {
            title: { display: true, text: 'Months', font: { size: 13, weight: 'bold' } },
            ticks: { stepSize: 3 },
          },
          y: {
            title: { display: true, text: 'Cumulative Cost ($)', font: { size: 13, weight: 'bold' } },
            ticks: {
              callback: (v) => '$' + v.toLocaleString(),
            },
            grid: {
              color: (ctx) => ctx.tick.value === 0 ? 'rgba(0,0,0,0.3)' : 'rgba(0,0,0,0.08)',
              lineWidth: (ctx) => ctx.tick.value === 0 ? 2 : 1,
            },
          },
        },
      },
    });

    return () => { if (chartRef.current) chartRef.current.destroy(); };
  }, [processes, contextData, chartSize]);

  const sizeMap = { small: '400px', medium: '600px', large: '900px' };

  if (!processes || processes.length === 0) return null;

  return (
    <Paper shadow="sm" p="xl" radius="md">
      <Group justify="space-between" mb="sm">
        <div>
          <Text fw={700} size="lg" c="purple">Break-Even Analysis</Text>
          <Text size="sm" c="dimmed">
            Compares cumulative cost of current operations (using Likely estimate as baseline) vs AI investment across all three scenarios.
            Break-even is where an AI scenario line crosses below the Current Doing Business line.
          </Text>
        </div>
        <Group gap="xs">
          {['small', 'medium', 'large'].map((s) => (
            <Button key={s} size="xs" variant={chartSize === s ? 'filled' : 'outline'} color="purple" onClick={() => setChartSize(s)}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </Button>
          ))}
        </Group>
      </Group>
      <Box style={{ maxWidth: sizeMap[chartSize], margin: '0 auto' }}>
        <canvas ref={canvasRef} />
      </Box>
    </Paper>
  );
}

export default ROIBreakEvenChart;
